import type { FormField } from '../../../types/snippets'

/**
 * Valida um valor de campo contra seu schema.
 * Retorna mensagem de erro ou `null` se válido.
 *
 * Extraído de SnippetFormModal para permitir testes isolados.
 */
export function validateField(field: FormField, value: string): string | null {
  if (field.field_type === 'radio' || field.field_type === 'checkbox') {
    // Radio sempre tem valor (default), checkbox não precisa validação
    return null
  }

  if (field.field_type === 'date') {
    if (!value.trim()) return field.optional ? null : 'Campo obrigatório'
    const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/
    const match = value.match(dateRegex)
    if (!match) return 'Formato inválido (DD/MM/AAAA)'
    const [, dd, mm, yyyy] = match
    const d = new Date(parseInt(yyyy!), parseInt(mm!) - 1, parseInt(dd!))
    if (
      d.getDate() !== parseInt(dd!) ||
      d.getMonth() + 1 !== parseInt(mm!) ||
      d.getFullYear() !== parseInt(yyyy!)
    ) {
      return 'Data inválida'
    }
    return null
  }

  if (!value.trim()) {
    return 'Campo obrigatório'
  }

  if (field.pattern) {
    const regex = new RegExp(field.pattern)
    if (!regex.test(value)) {
      return field.error_message || 'Formato inválido'
    }
  }

  if (field.maxlength && value.length > field.maxlength) {
    return `Máximo ${field.maxlength} caracteres (atual: ${value.length})`
  }

  return null
}

/**
 * Valor inicial de um campo de data: hoje deslocado por `default_offset_months` e
 * `default_offset_days`, em DD/MM/AAAA. Vazio quando o campo não declara deslocamento.
 */
export function defaultDateValue(
  field: FormField,
  today: Date = new Date()
): string {
  if (
    field.default_offset_months === undefined &&
    field.default_offset_days === undefined
  ) {
    return ''
  }
  const d = new Date(today)
  d.setMonth(d.getMonth() + (field.default_offset_months ?? 0))
  d.setDate(d.getDate() + (field.default_offset_days ?? 0))
  const dd = String(d.getDate()).padStart(2, '0')
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  return `${dd}/${mm}/${d.getFullYear()}`
}

/** Converte DD/MM/YYYY para Date. Retorna null se inválido. */
function parseDateBR(s: string): Date | null {
  const m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!m) return null
  const d = new Date(parseInt(m[3]!), parseInt(m[2]!) - 1, parseInt(m[1]!))
  if (isNaN(d.getTime())) return null
  return d
}

/**
 * Valida que o range entre `dateFrom` e `dateTo` não excede `maxDays`.
 * Retorna mensagem de erro ou `null` se válido.
 */
export function validateDateRange(
  dateFrom: string,
  dateTo: string,
  maxDays: number,
  errorMessage?: string
): string | null {
  const from = parseDateBR(dateFrom)
  const to = parseDateBR(dateTo)
  if (!from || !to) return null // datas inválidas já tratadas pelo validateField
  const diffDays = Math.round((to.getTime() - from.getTime()) / 86_400_000)
  if (diffDays < 0) return 'A data final não pode ser anterior à inicial.'
  if (diffDays > maxDays) {
    return (
      errorMessage ??
      `O período não pode exceder ${maxDays} dias (atual: ${diffDays} dias).`
    )
  }
  return null
}
